import { useEffect, useMemo, useState } from 'react'
import {
  FilterInput,
  HomeBanner,
  ListingBanner,
  ListingProduct
} from '@/network/graphql.g'
import { ListingData } from 'types/listingData'
import { BridgeProduct, convertAPIProductToProduct } from 'types/bridgeProduct'
import { ProductListingFetcher } from '@/components/organisms/productListing/productListingFetcher'
import { CategoryType } from '@/types/categoryType'
import { makeFilterInputFromQuery } from '@/providers/filterProvider/parsingFilters'
import { useRouter } from 'next/router'
import { getPageFromRouterOrDefault } from '@/utils/urlHelper'
import { ID } from 'graphql-ws'
import { Config } from '@/core/config'
import { getShallowParams } from '@/utils/routerHelpers'

type useProductListingProps = {
  totalPagesCount: number
  totalProductsCount: number
  id: ID
  listingBanners?: ListingBanner[]
  products: ListingProduct[]
  banners?: HomeBanner[]
  type?: CategoryType
  filterId: string
  requestId: string
  isPreload: boolean
  fetcher: ProductListingFetcher
  categoryName?: string
}

export const createFilterId = (
  filterInput: FilterInput[],
  page: number
): string => JSON.stringify([...filterInput, { page }])

const useProductListing = ({
  totalPagesCount,
  totalProductsCount,
  id,
  listingBanners,
  products,
  banners,
  type,
  filterId,
  requestId,
  isPreload,
  fetcher,
  categoryName
}: useProductListingProps): {
  data: ListingData<BridgeProduct>
} => {
  const router = useRouter()
  const createListingData = (
    products: BridgeProduct[],
    totalPagesCount: number,
    isPreload
  ): ListingData<BridgeProduct> => ({
    totalPagesCount,
    id,
    listingBanners,
    products,
    banners,
    type,
    filterId: router
      ? createFilterId(
          makeFilterInputFromQuery(router.query),
          getPageFromRouterOrDefault(router)
        )
      : filterId,
    isPreload,
    categoryName
  })

  useEffect(() => {
    // handled separately in wishlist
    if (router.query.na) {
      delete router.query.na
      const { url, as } = getShallowParams(router)
      router.replace(url, as, { shallow: true, scroll: false })
    }
  }, [])

  const initialListingData: ListingData<BridgeProduct> = useMemo(
    () =>
      createListingData(
        products?.map((product) => convertAPIProductToProduct(product)),
        totalPagesCount,
        isPreload
      ),
    [totalPagesCount, id, listingBanners, products, banners, type, filterId]
  )

  const [listingData, setListingData] = useState<ListingData<BridgeProduct>>()

  const data = useMemo(() => {
    return (
      (listingData?.id === id && listingData?.filterId === filterId
        ? listingData
        : null) ?? initialListingData
    )
  }, [id, listingData, initialListingData])

  useEffect(() => {
    const fetchFullPage = async () => {
      const data = await fetcher(
        getPageFromRouterOrDefault(router),
        makeFilterInputFromQuery(router.query),
        true,
        true
      )
      if (data) {
        setData(data.products, data.totalPagesCount)
      }
    }
    if (totalProductsCount > Config.perInitialPage && isPreload) {
      fetchFullPage()
    }
  }, [requestId])

  const setData = (products: BridgeProduct[], totalPagesCount: number) => {
    setListingData(createListingData(products, totalPagesCount, false))
  }

  useEffect(() => {
    const anchor = router.query.a as string
    if (anchor) {
      let i = 0
      const handler = setInterval(() => {
        if (i >= 20) {
          clearInterval(handler)
          return
        }
        const el = document.getElementById(anchor)
        if (el) {
          clearInterval(handler)
          el.scrollIntoView({ block: 'center' })
        }
        i++
      }, 500)
      return () => clearInterval(handler)
    }
  }, [data, router.query.a])

  useEffect(() => {
    if (location.hash) {
      let i = 0
      const handler = setInterval(() => {
        if (i >= 10) {
          clearInterval(handler)
          return
        }
        const el = document.getElementById(location.hash.replace('#', ''))
        if (el) {
          clearInterval(handler)
          const hash = location.hash
          location.hash = ''
          location.hash = hash
          el.removeAttribute('id')
        }
        i++
      }, 500)
      return () => clearInterval(handler)
    }
  }, [data])

  return { data }
}

export default useProductListing
